import { gql } from "@apollo/client";
import {
  APPLICANT_ACTIVITY_FIELDS,
  APPLICANT_RATE_FIELDS,
  MINIMAL_PROFILE_FIELDS,
  MINIMAL_MISSION_ROLE_FIELDS,
  CORE_MISSION_FIELDS,
  ATS_RESULT_FILTER_METADATA,
} from "graphql/fragments";

const GET_MISSION_ROLE_APPLICANTS = gql`
  ${ATS_RESULT_FILTER_METADATA}
  ${MINIMAL_PROFILE_FIELDS}
  ${APPLICANT_ACTIVITY_FIELDS}
  ${APPLICANT_RATE_FIELDS}
  ${MINIMAL_MISSION_ROLE_FIELDS}
  ${CORE_MISSION_FIELDS}
  query missionRoleApplicants(
    $filters: MissionRoleApplicantFilter
    $advancedFilters: [AdvancedFilter!]
    $first: Int
    $last: Int
    $after: String
    $before: String
    $orderBy: MissionRoleApplicantOrderBy
    $paginationInfo: PaginationInfo
    $includeSellingPriceEstimates: Boolean = false
    $includeScores: Boolean = true
    $includeApplicationChecklist: Boolean = false
    $resultFilterMetadata: [String!]
    $includeFlaggedCommentsCount: Boolean = false
    $includeMission: Boolean = true
  ) {
    missionRoleApplicants(
      filters: $filters
      advancedFilters: $advancedFilters
      first: $first
      last: $last
      after: $after
      before: $before
      orderBy: $orderBy
      paginationInfo: $paginationInfo
      resultFilterMetadata: $resultFilterMetadata
    ) {
      totalCount
      currentPage
      perPage
      requestedState
      filterMetadata @include(if: $includeScores) {
        ...MissionRoleParticipantResultFilterMetadata
      }
      nodes {
        id
        uuid
        state
        ...ApplicantActivityFields
        applicationChecklist @include(if: $includeApplicationChecklist)
        rateScore @include(if: $includeScores)
        matchScore @include(if: $includeScores)
        subScores
        createdAt
        updatedAt
        appliedAt
        noticePeriod
        availablePartTimeDuringNotice
        highlights {
          id
          name
        }
        organization {
          id
          slug
          name
        }
        profile {
          ...MinimalProfileFields
          flaggedCommentsCount @include(if: $includeFlaggedCommentsCount)
          status
          sellingPriceEstimate @include(if: $includeSellingPriceEstimates)
          partner {
            id
            name
          }
        }
        mission @include(if: $includeMission) {
          ...MissionFields
        }
        missionRole {
          state
          ...MinimalMissionRoleFields
        }
        profileData {
          archetype
          disciplines
          country
          continent
          region
          isAssigned
        }
        missionData {
          activeApplicationCount
          activeApplicationRoles {
            ...MinimalMissionRoleFields
          }
          invitationConversionRate
          missionCount
          appliedAt
          pendingSince
          isInvited
        }
        scoreData {
          activity {
            consideredDaysSinceLastActivity
          }
          experience {
            consideredExperienceYears
            rawExperienceYears
          }
          skills {
            matchingSkillCount
            matchingSkills
          }
          timeZone {
            consideredTimeZoneOffset
            rawTimeZoneOffset
          }
        }
        scores {
          matchActivityScore
          weightActivity
          matchExperienceScore
          weightExperience
          matchSkillsScore
          weightSkills
          matchTimeZoneScore
          weightTimeZone
          matchScorePrevious
          vectorRank
          vectorScore
        }
        hourlyRateData {
          consideredHourlyRateUsd
          consideredHourlyRateUsdDelta
          applicantHourlyRateUsdDelta
          applicantHourlyRateUsd
          minimumApplicantHourlyRateUsd
          minimumAssignedHourlyRateUsd
          missionRoleTargetRateUsd
          negotiatedHourlyRateUsd
          profileHourlyRateUsd
          currency
        }
        negotiatedRate {
          amount
          currency
          frequency
        }
        negotiatedSellingRate {
          amount
          currency
          frequency
        }
        sellingRateRange
        ...ApplicantRateFields
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      edges {
        cursor
        node {
          id
        }
      }
    }
  }
`;

const GET_MISSION_ROLE_APPLICANTS_SHARE_DATA = gql`
  query missionRoleApplicantsShareData($filters: MissionRoleApplicantFilter, $paginationInfo: PaginationInfo) {
    missionRoleApplicants(filters: $filters, paginationInfo: $paginationInfo) {
      nodes {
        id
        uuid
        negotiatedSellingRate {
          amount
          currency
          frequency
        }
        missionRole {
          name
          mission {
            name
            organization {
              applicationsPin
            }
          }
        }
        profile {
          name
          disciplines {
            name
          }
          skills {
            name
          }
          externalLinks {
            name
            type
            url
          }
          location
        }
      }
    }
  }
`;

const GET_MISSION_ROLE_APPLICANTS_COUNT = gql`
  query missionRoleApplicants($filters: MissionRoleApplicantFilter) {
    missionRoleApplicants(filters: $filters) {
      totalCount
    }
  }
`;

const GET_MISSION_ROLE_INVITATIONS = gql`
  ${ATS_RESULT_FILTER_METADATA}
  ${MINIMAL_PROFILE_FIELDS}
  ${MINIMAL_MISSION_ROLE_FIELDS}
  query missionRoleInvitations(
    $filters: MissionRoleInvitationFilter
    $advancedFilters: [AdvancedFilter!]
    $first: Int
    $last: Int
    $after: String
    $before: String
    $orderBy: MissionRoleInvitationOrderBy
    $paginationInfo: PaginationInfo
    $includeFlaggedCommentsCount: Boolean = false
    $resultFilterMetadata: [String!]
  ) {
    missionRoleInvitations(
      filters: $filters
      advancedFilters: $advancedFilters
      first: $first
      last: $last
      after: $after
      before: $before
      orderBy: $orderBy
      paginationInfo: $paginationInfo
      resultFilterMetadata: $resultFilterMetadata
    ) {
      totalCount
      currentPage
      perPage
      filterMetadata {
        ...MissionRoleParticipantResultFilterMetadata
      }
      nodes {
        id
        rateScore
        matchScore
        subScores
        invitedAt
        acceptedAt
        invitedBy {
          firstName
          lastName
        }
        profile {
          ...MinimalProfileFields
          flaggedCommentsCount @include(if: $includeFlaggedCommentsCount)
          status
          partner {
            id
            name
          }
        }
        missionRole {
          ...MinimalMissionRoleFields
        }
        profileData {
          archetype
          disciplines
          country
          continent
          region
        }
        missionData {
          activeApplicationCount
          activeApplicationRoles {
            ...MinimalMissionRoleFields
          }
          invitationConversionRate
          missionCount
          invitedAt
        }
        scoreData {
          activity {
            consideredDaysSinceLastActivity
          }
          experience {
            consideredExperienceYears
            rawExperienceYears
          }
          skills {
            matchingSkillCount
            matchingSkills
          }
          timeZone {
            consideredTimeZoneOffset
            rawTimeZoneOffset
          }
        }
        scores {
          matchActivityScore
          weightActivity
          matchExperienceScore
          weightExperience
          matchSkillsScore
          weightSkills
          matchTimeZoneScore
          weightTimeZone
          matchScorePrevious
          vectorRank
          vectorScore
        }
        hourlyRateData {
          consideredHourlyRateUsd
          consideredHourlyRateUsdDelta
          minimumApplicantHourlyRateUsd
          minimumAssignedHourlyRateUsd
          missionRoleTargetRateUsd
          profileHourlyRateUsd
          currency
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      edges {
        cursor
        node {
          id
        }
      }
    }
  }
`;

const GET_MISSION_ROLE_ELIGIBILITY = gql`
  query missionApplicantEligibilityCheck($profileId: ID!, $missionRoleId: ID!) {
    missionApplicantEligibilityCheck(profileId: $profileId, missionRoleId: $missionRoleId) {
      eligible
      unfulfilledPolicies
    }
  }
`;

const GET_MISSION_ROLE_RECOMMENDATION = gql`
  query missionRoleApplicantRecommendation($profileId: ID, $missionRoleId: ID!) {
    missionRoleApplicantRecommendation(profileId: $profileId, missionRoleId: $missionRoleId) {
      recommendedRate
      recommendedRateCurrency
      averageExperience
      applicantsCount
    }
  }
`;

const GET_MISSION_ROLE_RECOMMENDATIONS = gql`
  ${ATS_RESULT_FILTER_METADATA}
  ${MINIMAL_PROFILE_FIELDS}
  ${MINIMAL_MISSION_ROLE_FIELDS}
  query missionRoleRecommendations(
    $filters: MissionRoleRecommendationFilter!
    $advancedFilters: [AdvancedFilter!]
    $orderBy: MissionRoleRecommendationOrderBy
    $first: Int
    $last: Int
    $after: String
    $before: String
    $paginationInfo: PaginationInfo
    $includeSellingPriceEstimates: Boolean = false
    $includeFlaggedCommentsCount: Boolean = false
    $resultFilterMetadata: [String!]
  ) {
    missionRoleRecommendations(
      filters: $filters
      advancedFilters: $advancedFilters
      orderBy: $orderBy
      first: $first
      last: $last
      after: $after
      before: $before
      paginationInfo: $paginationInfo
      resultFilterMetadata: $resultFilterMetadata
    ) {
      totalCount
      currentPage
      perPage
      filterMetadata {
        ...MissionRoleParticipantResultFilterMetadata
      }
      nodes {
        rateScore
        matchScore
        subScores
        profile {
          ...MinimalProfileFields
          flaggedCommentsCount @include(if: $includeFlaggedCommentsCount)
          status
          sellingPriceEstimate @include(if: $includeSellingPriceEstimates)
          partner {
            id
            name
          }
        }
        missionRole {
          ...MinimalMissionRoleFields
          state
        }
        profileData {
          archetype
          disciplines
          country
          continent
          region
          isAssigned
        }
        missionData {
          activeApplicationCount
          activeApplicationRoles {
            ...MinimalMissionRoleFields
          }
          invitationConversionRate
          missionCount
        }
        scoreData {
          activity {
            consideredDaysSinceLastActivity
          }
          experience {
            consideredExperienceYears
            rawExperienceYears
          }
          skills {
            matchingSkillCount
            matchingSkills
          }
          timeZone {
            consideredTimeZoneOffset
            rawTimeZoneOffset
          }
        }
        scores {
          matchActivityScore
          weightActivity
          matchExperienceScore
          weightExperience
          matchSkillsScore
          weightSkills
          matchTimeZoneScore
          weightTimeZone
          matchScorePrevious
          vectorRank
          vectorScore
        }
        hourlyRateData {
          consideredHourlyRateUsd
          consideredHourlyRateUsdDelta
          minimumApplicantHourlyRateUsd
          minimumAssignedHourlyRateUsd
          missionRoleTargetRateUsd
          profileHourlyRateUsd
          currency
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      edges {
        node {
          profile {
            id
          }
        }
      }
    }
  }
`;

const GET_DATA_SERVICE_APPLICANTS_COUNT = gql`
  query dataServiceApplicantsCount(
    $filters: MissionRoleApplicantFilter
    $advancedFilters: [AdvancedFilter!]
    $states: [MissionRoleApplicantStateEnum!]!
  ) {
    dataServiceApplicantsCount(filters: $filters, advancedFilters: $advancedFilters, states: $states)
  }
`;

const GET_DATA_SERVICE_INVITATIONS_COUNT = gql`
  query dataServiceInvitationsCount($filters: MissionRoleInvitationFilter, $advancedFilters: [AdvancedFilter!]) {
    dataServiceInvitationsCount(filters: $filters, advancedFilters: $advancedFilters) {
      count
    }
  }
`;

export {
  GET_MISSION_ROLE_APPLICANTS,
  GET_MISSION_ROLE_INVITATIONS,
  GET_DATA_SERVICE_APPLICANTS_COUNT,
  GET_DATA_SERVICE_INVITATIONS_COUNT,
  GET_MISSION_ROLE_RECOMMENDATIONS,
  GET_MISSION_ROLE_ELIGIBILITY,
  GET_MISSION_ROLE_RECOMMENDATION,
  GET_MISSION_ROLE_APPLICANTS_COUNT,
  GET_MISSION_ROLE_APPLICANTS_SHARE_DATA,
};
